import { useEffect, useState } from "react";
import { Crown, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

export default function NoblePacks() {
  const [tiers, setTiers] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/noble/config");
      setTiers(data?.tiers || []);
      setSource(data?.source || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const isLive = source === "live";

  return (
    <div className="space-y-6" data-testid="noble-packs-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Qwick Live</p>
          <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Noble Packs</h1>
          <p className="text-zinc-500 mt-1 max-w-2xl">
            Noble tiers, pack IDs and diamond costs are pulled live from Qwick Live (Noble.ListAllNobleConf) and used
            automatically for recharges — no manual setup needed.
          </p>
        </div>
        <Button onClick={refresh} disabled={loading} variant="outline" data-testid="refresh-noble-packs">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
          isLive ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
        }`}
        data-testid="noble-source-badge"
      >
        {isLive ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
        {isLive ? "Synced live from Qwick Live" : "Showing fallback values (live session unavailable — check Session Cookie)"}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              {["Tier", "noble_type", "First-time 💎", "First pack id", "Renewal 💎", "Renewal pack id"].map((h) => (
                <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tiers.length === 0 && (
              <tr><td colSpan="6" className="px-6 py-12 text-center text-zinc-400 text-sm">{loading ? "Loading…" : "No tiers available"}</td></tr>
            )}
            {tiers.map((t) => (
              <tr key={t.key} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`noble-tier-row-${t.key}`}>
                <td className="px-6 py-4 text-sm font-medium text-zinc-900">
                  <span className="inline-flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" />{t.label}</span>
                </td>
                <td className="px-6 py-4 text-sm text-zinc-700 font-mono">{t.noble_type}</td>
                <td className="px-6 py-4 text-sm text-zinc-900 tabular-nums font-mono">{(t.first?.diamond ?? 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-zinc-500 font-mono">{t.first?.turnover_pack_id || "—"}</td>
                <td className="px-6 py-4 text-sm text-zinc-900 tabular-nums font-mono">{(t.renew?.diamond ?? 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-zinc-500 font-mono">{t.renew?.turnover_pack_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
